/**
 * Money handling.
 *
 * Every monetary amount in this system is stored and transported as an integer
 * number of **minor units** (paise for INR). Floating point never touches a
 * financial figure — a ₹35,40,000.00 invoice is the integer 354000000.
 */
import { Currency } from './enums.js';
const MINOR_PER_MAJOR = 100;
/** Convert a major-unit value (rupees) to minor units (paise). */
export function toMinor(major) {
    const value = typeof major === 'string' ? Number(major.replace(/[,\s₹]/g, '')) : major;
    if (!Number.isFinite(value))
        throw new TypeError(`Not a numeric amount: ${String(major)}`);
    return Math.round(value * MINOR_PER_MAJOR);
}
/** Convert minor units back to a major-unit number. For display/export only. */
export function fromMinor(minor) {
    return minor / MINOR_PER_MAJOR;
}
export function addMinor(...values) {
    return values.reduce((sum, value) => sum + value, 0);
}
export function subMinor(a, b) {
    return a - b;
}
/** Absolute difference — used by tolerance checks. */
export function diffMinor(a, b) {
    return Math.abs(a - b);
}
/**
 * True when two amounts agree within an absolute tolerance (default ₹1) or a
 * relative tolerance (default 0.5%), whichever is larger. Used for the
 * `subtotal + tax ≈ total` check (PRD §13) and for bank amount matching.
 */
export function amountsMatch(a, b, options = {}) {
    const { absoluteTolerance = 100, relativeTolerance = 0.005 } = options;
    const tolerance = Math.max(absoluteTolerance, Math.abs(a) * relativeTolerance);
    return diffMinor(a, b) <= tolerance;
}
const INR_FORMATTER = new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
});
/** ₹35,40,000.00 */
export function formatINR(minor) {
    return INR_FORMATTER.format(fromMinor(minor));
}
/**
 * Compact Indian-numbering display used across dashboards and list screens:
 * ₹6.20 Cr, ₹35.40 L, ₹82,000.
 */
export function formatCompactINR(minor) {
    const major = fromMinor(minor);
    const sign = major < 0 ? '-' : '';
    const abs = Math.abs(major);
    if (abs >= 1_00_00_000)
        return `${sign}₹${trim(abs / 1_00_00_000)} Cr`;
    if (abs >= 1_00_000)
        return `${sign}₹${trim(abs / 1_00_000)} L`;
    if (abs >= 1_000)
        return `${sign}₹${trim(abs / 1_000)} K`;
    return `${sign}₹${abs.toFixed(0)}`;
}
function trim(value) {
    return value.toFixed(2).replace(/\.00$/, '');
}
const FORMATTERS = new Map();
function formatterFor(currency) {
    let formatter = FORMATTERS.get(currency);
    if (!formatter) {
        formatter = new Intl.NumberFormat(currency === Currency.INR ? 'en-IN' : 'en-US', {
            style: 'currency',
            currency,
            currencyDisplay: currency === Currency.INR ? 'symbol' : 'code',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        FORMATTERS.set(currency, formatter);
    }
    return formatter;
}
/**
 * An amount in its own currency: ₹35,40,000.00, AED 12,500.00, USD 980.00.
 *
 * Rupees keep Indian grouping and the ₹ sign; every other currency is shown
 * with its ISO code, because "$" alone does not say which dollar.
 */
export function formatMoney(minor, currency = Currency.INR) {
    if (currency === Currency.INR)
        return formatINR(minor);
    // `Intl` puts a non-breaking space after the code; a plain one copies cleanly.
    return formatterFor(currency)
        .format(fromMinor(minor))
        .replace(new RegExp(String.fromCharCode(0xa0), 'g'), ' ');
}
/** Compact display: ₹6.20 Cr for rupees, AED 1.25 M for everything else. */
export function formatCompactMoney(minor, currency = Currency.INR) {
    if (currency === Currency.INR)
        return formatCompactINR(minor);
    const major = fromMinor(minor);
    const sign = major < 0 ? '-' : '';
    const abs = Math.abs(major);
    if (abs >= 1_000_000_000)
        return `${sign}${currency} ${trim(abs / 1_000_000_000)} B`;
    if (abs >= 1_000_000)
        return `${sign}${currency} ${trim(abs / 1_000_000)} M`;
    if (abs >= 1_000)
        return `${sign}${currency} ${trim(abs / 1_000)} K`;
    return `${sign}${currency} ${abs.toFixed(0)}`;
}
/**
 * The INR equivalent of an amount, for display only.
 *
 * `inrRate` is rupees per one unit of the amount's currency (22.6 for AED): the
 * latest reference rate, fetched live and never booked, so nothing in the
 * workflow — approvals, payments, matching — ever reads the result.
 */
export function toInrMinor(minor, inrRate) {
    if (!Number.isFinite(inrRate) || inrRate <= 0)
        throw new TypeError('INR rate must be positive');
    return Math.round(minor * inrRate);
}
const BASIS_POINTS_PER_UNIT = 10_000;
export function percentToBasisPoints(percent) {
    if (!Number.isFinite(percent))
        throw new TypeError(`Not a numeric rate: ${String(percent)}`);
    return Math.round(percent * 100);
}
export function basisPointsToPercent(basisPoints) {
    return basisPoints / 100;
}
/** `10%`, `0.75%` — trailing zeros trimmed, as the rate is displayed on screen. */
export function formatBasisPoints(basisPoints) {
    return `${String(basisPointsToPercent(basisPoints))}%`;
}
/**
 * Apply a basis-point rate to a minor-unit amount, rounding half up.
 *
 * The intermediate product stays exact for any amount this product can hold:
 * ₹100 Cr at 100% is 10^15, comfortably inside the safe integer range.
 */
export function applyRate(amount, basisPoints) {
    if (!Number.isInteger(amount))
        throw new TypeError('Amount must be an integer minor value');
    if (!Number.isInteger(basisPoints))
        throw new TypeError('Rate must be integer basis points');
    return Math.round((amount * basisPoints) / BASIS_POINTS_PER_UNIT);
}
/** ISO codes and written-out markers that may sit beside an amount: "AED 1,200", "Rs.500". */
const CURRENCY_MARKS = new RegExp(`\\b(?:${Object.values(Currency).join('|')})\\b|Rs\\.?|د\\.إ`, 'gi');
/** Parse amounts as they appear in invoices and bank statements. */
export function parseAmountToMinor(input) {
    if (input === null || input === undefined || input === '')
        return null;
    if (typeof input === 'number')
        return Number.isFinite(input) ? toMinor(input) : null;
    const raw = String(input).trim();
    if (!raw)
        return null;
    // Strip currency symbols, spaces, and thousands separators (Indian or
    // Western grouping); keep a trailing minus or parenthesised negative.
    const negative = /^\(.*\)$/.test(raw) || raw.trim().endsWith('-') || raw.trim().startsWith('-');
    const cleaned = raw
        .replace(CURRENCY_MARKS, '')
        .replace(/[()₹$€£,\s]/g, '')
        .replace(/-/g, '');
    if (!/^\d*\.?\d*$/.test(cleaned) || cleaned === '' || cleaned === '.')
        return null;
    const value = Number(cleaned);
    if (!Number.isFinite(value))
        return null;
    return toMinor(negative ? -value : value);
}
//# sourceMappingURL=money.js.map